import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeftRight, Check, X } from "lucide-react";

export default function TransferRequests() {
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("request");
  const [box, setBox] = useState("incoming");
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState("");
  const highlightRef = useRef(null);

  const load = useCallback(() => {
    api.get("/person-match/transfer-requests", { params: { box } })
      .then((r) => setRows(r.data.requests || [])).catch((e) => toast.error(apiError(e)));
  }, [box]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (highlightId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId, rows]);

  const decide = async (id, approve) => {
    setBusy(id);
    try {
      await api.post(`/person-match/transfer-requests/${id}/${approve ? "approve" : "deny"}`, { notes: notes[id] || "" });
      toast.success(approve ? "Transfer approved" : "Request denied");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="transfer-requests-page">
      <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-[#0D9488]" /> Person Transfer / Access Requests</h1>
      <p className="text-sm text-slate-500 mt-0.5">Cross-organization requests to access or transfer an existing Person within this account. Approving transfers organization ownership — never duplicates the Person.</p>

      <div className="flex gap-1 border-b border-slate-200 mt-4">
        {[["incoming", "Incoming"], ["outgoing", "My Requests"]].map(([k, l]) => (
          <button key={k} onClick={() => setBox(k)} data-testid={`tr-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${box === k ? "border-[#006090] text-[#006090]" : "border-transparent text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {rows.length === 0 ? <p className="text-sm text-slate-400 mt-8">No {box} requests.</p> : (
        <div className="mt-5 space-y-3">
          {rows.map((r) => (
            <div key={r.id} ref={r.id === highlightId ? highlightRef : null}
              className={`rounded-xl border bg-white p-4 transition-shadow ${r.id === highlightId ? "border-[#006090] ring-2 ring-[#006090]/30" : "border-slate-200"}`}
              data-testid={`tr-row-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-900">{r.person_name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "pending" ? "bg-amber-50 text-amber-700" : r.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{r.status}</span>
              </div>
              <div className="text-xs text-slate-600 mt-1">
                Requested by <strong>{r.requesting_user_name}</strong> ({r.requesting_org_name || "—"}) · currently owned by <strong>{r.owning_org_name || "—"}</strong>
              </div>
              {r.reason && <p className="text-sm text-slate-700 mt-2 italic">“{r.reason}”</p>}
              {r.status !== "pending" && r.decided_by_name && <p className="text-xs text-slate-400 mt-2">Decided by {r.decided_by_name}{r.decision_notes ? ` — ${r.decision_notes}` : ""}</p>}
              {box === "incoming" && r.status === "pending" && (
                <div className="mt-3 space-y-2">
                  <Textarea rows={1} placeholder="Decision note (optional)" value={notes[r.id] || ""} onChange={(e) => setNotes((p) => ({ ...p, [r.id]: e.target.value }))} data-testid={`tr-notes-${r.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" disabled={busy === r.id} onClick={() => decide(r.id, true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`tr-approve-${r.id}`}><Check className="w-4 h-4 mr-1" />Approve transfer</Button>
                    <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => decide(r.id, false)} data-testid={`tr-deny-${r.id}`}><X className="w-4 h-4 mr-1" />Deny</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
